import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import apiClient from "@/api/apiClient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Heart,
  Mail,
  Shield,
  Building,
  User,
  Phone,
  MapPin,
} from "lucide-react";

export default function PracticeOwnerSignup() {
  const [formData, setFormData] = useState({
    email: "",
    firstName: "",
    lastName: "",
    practiceName: "",
    practiceAddress: "",
    practicePhone: "",
    specializations: "",
  });
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: form, 2: verification
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      // Send the practice owner signup request
      await apiClient.post("/api/auth/practice-owner/signup", formData);
      setStep(2);
      setSuccess("Verification code sent to your email!");
    } catch (err) {
      // Handle specific error responses from the server
      if (err.data?.error) {
        setError(err.data.error);
      } else if (err.status === 403) {
        setError(
          "Your email address is not authorized for practice owner registration. Please contact support to have your practice email approved before signing up."
        );
      } else {
        setError(err.message || "Failed to create practice owner account");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async (e) => {
    e.preventDefault();
    setError("");
    setIsLoading(true);

    try {
      await apiClient.post("/api/auth/practice-owner/verify", {
        email: formData.email,
        code,
      });

      // Invalidate current user query to trigger refetch
      await queryClient.invalidateQueries(["currentUser"]);
      await queryClient.refetchQueries(["currentUser"]);

      // Redirect to dashboard
      navigate("/");
    } catch (err) {
      // Handle specific error responses from the server
      if (err.data?.error) {
        setError(err.data.error);
      } else {
        setError(err.message || "Verification failed");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendCode = async () => {
    setError("");
    setSuccess("");
    setIsLoading(true);

    try {
      await apiClient.post("/api/auth/practice-owner/resend-code", {
        email: formData.email,
      });
      setSuccess("New verification code sent to your email!");
      setCode("");
    } catch (err) {
      // Handle specific error responses from the server
      if (err.data?.error) {
        setError(err.data.error);
      } else {
        setError(err.message || "Failed to resend verification code");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToForm = () => {
    setStep(1);
    setCode("");
    setError("");
    setSuccess("");
  };

  if (step === 2) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <Heart className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold text-slate-900">
              ClubMed Practice
            </CardTitle>
            <p className="text-slate-600">Enter verification code</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleVerifyCode} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription className="flex items-start gap-2">
                    <div className="mt-0.5">❌</div>
                    <div>{error}</div>
                  </AlertDescription>
                </Alert>
              )}
              {success && (
                <Alert className="border-green-200 bg-green-50">
                  <AlertDescription className="text-green-800">
                    {success}
                  </AlertDescription>
                </Alert>
              )}

              <div className="space-y-2">
                <label
                  htmlFor="code"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <Shield className="w-4 h-4" />
                  Verification Code
                </label>
                <Input
                  id="code"
                  type="text"
                  data-testid="signup-code-input"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  placeholder="Enter 6-character code"
                  maxLength={6}
                  className="text-center text-lg tracking-widest"
                  required
                />
                <p className="text-xs text-slate-500">
                  Check your email for the verification code sent to{" "}
                  <span className="font-medium">{formData.email}</span>
                </p>
              </div>

              <Button type="submit" className="w-full" disabled={isLoading} data-testid="signup-verify-btn">
                {isLoading
                  ? "Verifying..."
                  : "Verify & Create Practice Account"}
              </Button>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleResendCode}
                  disabled={isLoading}
                >
                  {isLoading ? "Sending..." : "Resend Code"}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={handleBackToForm}
                  disabled={isLoading}
                >
                  Back to Form
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-8">
      <Card className="w-full max-w-2xl">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <Heart className="w-8 h-8 text-white" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-slate-900">
            ClubMed Practice Registration
          </CardTitle>
          <p className="text-slate-600">Create your practice owner account</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmitForm} className="space-y-6">
            {error && (
              <Alert variant="destructive" className="mb-4">
                <AlertDescription className="flex items-start gap-2">
                  <div className="mt-0.5">⚠️</div>
                  <div>{error}</div>
                </AlertDescription>
              </Alert>
            )}

            {/* Practice Owner Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <User className="w-5 h-5" />
                Practice Owner Information
              </h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label
                    htmlFor="firstName"
                    className="text-sm font-medium text-slate-700"
                  >
                    First Name
                  </label>
                  <Input
                    id="firstName"
                    name="firstName"
                    type="text"
                    data-testid="signup-firstname-input"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    placeholder="John"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <label
                    htmlFor="lastName"
                    className="text-sm font-medium text-slate-700"
                  >
                    Last Name
                  </label>
                  <Input
                    id="lastName"
                    name="lastName"
                    type="text"
                    data-testid="signup-lastname-input"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    placeholder="Smith"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <label
                    htmlFor="email"
                    className="text-sm font-medium text-slate-700 flex items-center gap-2"
                  >
                    <Mail className="w-4 h-4" />
                    Email Address
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    data-testid="signup-email-input"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="doctor@example.com"
                    required
                  />
                  <p className="text-xs text-slate-500">
                    This email must be pre-approved for practice owner
                    registration
                  </p>
                </div>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="specializations"
                  className="text-sm font-medium text-slate-700"
                >
                  Specializations
                </label>
                <Input
                  id="specializations"
                  name="specializations"
                  type="text"
                  data-testid="signup-specializations-input"
                  value={formData.specializations}
                  onChange={handleInputChange}
                  placeholder="e.g., Cardiology, Internal Medicine"
                />
                <p className="text-xs text-slate-500">
                  Separate multiple specializations with commas
                </p>
              </div>
            </div>

            {/* Practice Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                <Building className="w-5 h-5" />
                Practice Information
              </h3>

              <div className="space-y-2">
                <label
                  htmlFor="practiceName"
                  className="text-sm font-medium text-slate-700"
                >
                  Practice Name
                </label>
                <Input
                  id="practiceName"
                  name="practiceName"
                  type="text"
                  data-testid="signup-practicename-input"
                  value={formData.practiceName}
                  onChange={handleInputChange}
                  placeholder="Smith Medical Center"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="practiceAddress"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <MapPin className="w-4 h-4" />
                  Practice Address
                </label>
                <Input
                  id="practiceAddress"
                  name="practiceAddress"
                  type="text"
                  data-testid="signup-address-input"
                  value={formData.practiceAddress}
                  onChange={handleInputChange}
                  placeholder="123 Medical Drive, City, State 12345"
                  required
                />
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="practicePhone"
                  className="text-sm font-medium text-slate-700 flex items-center gap-2"
                >
                  <Phone className="w-4 h-4" />
                  Practice Phone
                </label>
                <Input
                  id="practicePhone"
                  name="practicePhone"
                  type="tel"
                  data-testid="signup-phone-input"
                  value={formData.practicePhone}
                  onChange={handleInputChange}
                  placeholder="(555) 123-4567"
                  required
                />
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-medium text-blue-900 mb-2">
                Important Notes:
              </h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>
                  • Your email must be pre-approved for practice owner
                  registration
                </li>
                <li>
                  • You will receive a verification code to complete the
                  registration
                </li>
                <li>
                  • Once verified, you can invite doctors to join your practice
                </li>
                <li>• All practice data will be confidential and secure</li>
              </ul>
            </div>

            <Button type="submit" className="w-full" disabled={isLoading} data-testid="signup-submit-btn">
              {isLoading
                ? "Creating Account..."
                : "Create Practice Owner Account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{" "}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in here
              </Link>
            </p>
            <p className="text-sm text-slate-600 mt-2">
              Are you a patient?{" "}
              <Link
                to="/login"
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Patient login
              </Link>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
